// ProfileHero Component - Clubhouse 2.0 Design
// Heroic header section with gradient background, avatar, stats, and tier badge

import { motion } from 'framer-motion';
import { Crown, Calendar, Target, TrendingUp } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

interface ProfileHeroProps {
  name: string;
  memberSince?: string;
  avatarUrl?: string;
  tier?: 'free' | 'pro' | 'elite';
  stats?: {
    swingsAnalyzed: number;
    drillsCompleted: number;
    currentStreak: number;
    badgesEarned: number;
  };
}

export function ProfileHero({
  name,
  memberSince,
  avatarUrl,
  tier = 'free',
  stats = {
    swingsAnalyzed: 0,
    drillsCompleted: 0,
    currentStreak: 0,
    badgesEarned: 0,
  },
}: ProfileHeroProps) {
  const tierConfig = {
    free: {
      label: 'Free',
      color: 'bg-gray-500',
      gradient: 'from-gray-400 to-gray-600',
    },
    pro: {
      label: 'Pro',
      color: 'bg-[#F9C94E]',
      gradient: 'from-[#F9C94E] to-[#EAB308]',
    },
    elite: {
      label: 'Elite',
      color: 'bg-gradient-to-r from-purple-500 to-pink-500',
      gradient: 'from-purple-500 to-pink-500',
    },
  };

  const config = tierConfig[tier];

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-[#0D382A] via-[#196644] to-[#2B8A67] rounded-3xl shadow-xl mb-6">
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(167,243,161,0.3),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(249,201,78,0.2),transparent_50%)]" />
      </div>

      {/* Content */}
      <div className="relative px-6 py-8">
        {/* Avatar & Info */}
        <div className="flex flex-col items-center text-center mb-8">
          {/* Avatar with gradient ring */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="relative mb-4"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#A7F3A1] to-[#F9C94E] rounded-full blur-md opacity-60 animate-pulse" />
            <div className="relative bg-[radial-gradient(circle_at_center,#A7F3A1_0%,#F9C94E_100%)] p-1.5 rounded-full">
              <Avatar className="w-28 h-28 border-4 border-white/20 bg-white/10">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
                ) : (
                  <AvatarFallback className="text-3xl bg-transparent text-white font-bold">
                    {name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>
          </motion.div>

          {/* Name & Tier */}
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <h1 className="text-2xl font-bold text-white mb-2">{name}</h1>
            <div className="flex items-center justify-center gap-2">
              <Badge className={`${config.color} text-white border-0 px-2 py-0.5 rounded-full`}>
                {tier !== 'free' && <Crown className="w-3 h-3 mr-1" />}
                {config.label}
              </Badge>
              {memberSince && (
                <span className="text-white/70 text-sm flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {memberSince}
                </span>
              )}
            </div>
          </motion.div>
        </div>

        {/* Stats Grid */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="grid grid-cols-4 gap-3"
        >
          <StatCard
            icon={Target}
            value={stats.swingsAnalyzed}
            label="Swings"
            color="from-[#A7F3A1] to-[#3DC68D]"
          />
          <StatCard
            icon={TrendingUp}
            value={stats.drillsCompleted}
            label="Drills"
            color="from-[#F9C94E] to-[#EAB308]"
          />
          <StatCard
            icon={Calendar}
            value={stats.currentStreak}
            label="Streak"
            color="from-orange-400 to-orange-600"
            suffix="d"
          />
          <StatCard
            icon={Crown}
            value={stats.badgesEarned}
            label="Badges"
            color="from-purple-400 to-purple-600"
          />
        </motion.div>
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  value: number;
  label: string;
  color: string;
  suffix?: string;
}

function StatCard({ icon: Icon, value, label, color, suffix = '' }: StatCardProps) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 border border-white/20">
      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mb-2 mx-auto`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <p className="text-white text-xl font-bold text-center leading-none mb-0.5">
        {value}{suffix}
      </p>
      <p className="text-white/70 text-xs text-center">{label}</p>
    </div>
  );
}

